import { useEffect, useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, InputSuelto, Select, SelectSuelto } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { SelectorDeIcono } from '@/components/SelectorDeIcono';
import {
  useBorrarFormulario, useCamposSugeridos, useCatalogos, useDestinos, useFormulario,
  useGuardarFormulario, type Campo, type TipoBasico,
} from './api';

/**
 * DisenadorDeFormulario — qué se pregunta y a dónde va lo que llega.
 *
 * ## Lo que cambia
 *
 * - **El alta de personas nuevas no puede ser automática, y ahora se ve por
 *   qué.** El vanilla deshabilitaba la opción «Auto» por dentro del `<select>`;
 *   aquí el motivo está escrito debajo del campo.
 * - **Eliminar el formulario no cierra el diseñador para preguntar.**
 */

/** Los tipos que admite un formulario de recolección: los básicos menos
 *  «Empleado», que aquí no tiene sentido — lo diligencia alguien de fuera. */
const TIPOS: Array<[TipoBasico, string]> = [
  ['text', 'Texto'], ['textarea', 'Párrafo'], ['number', 'Número'], ['date', 'Fecha'],
  ['email', 'Correo'], ['phone', 'Teléfono'], ['select', 'Lista'],
];

export function DisenadorDeFormulario({ id, onCerrar }: { id: number | null; onCerrar: () => void }) {
  const consulta = useFormulario(id);
  const destinos = useDestinos(true);
  const catalogos = useCatalogos(true);
  const guardar = useGuardarFormulario();
  const borrar = useBorrarFormulario();
  const sugerir = useCamposSugeridos();

  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [icono, setIcono] = useState('bi-input-cursor-text');
  const [color, setColor] = useState('#0D2B4E');
  const [destino, setDestino] = useState('informativo');
  const [recursoDestino, setRecursoDestino] = useState<number | null>(null);
  const [modo, setModo] = useState<'manual' | 'auto'>('manual');
  const [correos, setCorreos] = useState('');
  const [campos, setCampos] = useState<Campo[]>([]);
  const [error, setError] = useState('');
  const [borrando, setBorrando] = useState(false);

  const f = consulta.data;
  // Seguridad: el alta de cuentas siempre pasa por una persona.
  const exigeManual = destino === 'maestro_nuevo';

  useEffect(() => {
    if (!f) return;
    setNombre(f.name);
    setDescripcion(f.description ?? '');
    setIcono(f.icon || 'bi-input-cursor-text');
    setColor(f.color || '#0D2B4E');
    setDestino(f.target_kind);
    setRecursoDestino(f.target_resource_id ?? null);
    setModo(f.approval_mode);
    setCorreos(f.notify_emails ?? '');
    setCampos((f.fields ?? []).map((c) => ({ ...c })));
  }, [f]);

  function enviar() {
    const limpios = campos.filter((c) => (c.label || '').trim());
    if (!nombre.trim()) { setError('Escribe el nombre del formulario.'); return; }
    if (!limpios.length) { setError('Agrega al menos un campo con etiqueta.'); return; }
    guardar.mutate(
      {
        id,
        cuerpo: {
          name: nombre.trim(),
          description: descripcion.trim() || null,
          icon: icono || 'bi-input-cursor-text',
          color,
          target_kind: destino,
          target_resource_id: destino === 'resource' ? recursoDestino : null,
          approval_mode: exigeManual ? 'manual' : modo,
          notify_emails: correos.trim() || null,
          fields: limpios,
        },
      },
      {
        onSuccess: () => { Utils.toast('Formulario guardado', 'success'); onCerrar(); },
        onError: (err) => setError(err instanceof Error ? err.message : 'No se pudo guardar'),
      },
    );
  }

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v && !guardar.isPending) onCerrar(); }}
      titulo={id ? 'Editar el formulario' : 'Nuevo formulario de recolección'}
      descripcion="Lo que se pregunta, a dónde va y quién lo aprueba."
      ancho="lg"
      pie={
        <>
          {id != null && (
            <Button variant="ghost" className="mr-auto text-danger-ink" onClick={() => setBorrando(true)}>
              <i className="bi bi-trash" aria-hidden="true" /> Eliminar
            </Button>
          )}
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={guardar.isPending}>
            <i className="bi bi-check-lg" aria-hidden="true" /> Guardar formulario
          </Button>
        </>
      }
    >
      {id != null && consulta.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      ) : id != null && consulta.isError ? (
        <EstadoError err={consulta.error} que="el formulario" onReintentar={() => void consulta.refetch()} />
      ) : (
        <div className="flex flex-col gap-4">
          <Field label="Nombre" error={error || undefined}>
            <Input
              value={nombre}
              onChange={(e) => { setNombre(e.target.value); setError(''); }}
              placeholder="Ej: Registro de nuevo colaborador"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <Field label="Ícono">
              <SelectorDeIcono
                id="df-icono" valor={icono} onCambio={setIcono} respaldo="bi-input-cursor-text"
              />
            </Field>
            <Field label="Color">
              <input
                type="color"
                aria-label="Color de la portada"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-11 w-16 cursor-pointer rounded-sm border border-field-border bg-surface p-1"
              />
            </Field>
          </div>

          <Field label="Descripción" opcional>
            <Input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Destino de la información" ayuda="A dónde se aplica lo que llegue.">
              <Select value={destino} onChange={(e) => setDestino(e.target.value)}>
                {(destinos.data?.kinds ?? []).map((k) => (
                  <option key={k.key} value={k.key}>{k.label}</option>
                ))}
              </Select>
            </Field>
            {destino === 'resource' && (
              <Field label="Base de datos">
                <Select
                  value={recursoDestino == null ? '' : String(recursoDestino)}
                  onChange={(e) => setRecursoDestino(Number(e.target.value) || null)}
                >
                  <option value="">— elige una —</option>
                  {(destinos.data?.resources ?? []).map((r) => (
                    <option key={r.id} value={String(r.id)}>{r.name}</option>
                  ))}
                </Select>
              </Field>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Modo de aprobación"
              ayuda={exigeManual
                ? 'El registro de personas nuevas crea una cuenta: siempre lo revisa alguien.'
                : undefined}
            >
              <Select
                value={exigeManual ? 'manual' : modo}
                disabled={exigeManual}
                onChange={(e) => setModo(e.target.value === 'auto' ? 'auto' : 'manual')}
              >
                <option value="manual">Manual (Bienestar revisa y aprueba)</option>
                <option value="auto">Auto (se aplica al enviar, sin revisión)</option>
              </Select>
            </Field>
            <Field
              label="Notificar a"
              opcional
              ayuda="Correos separados por coma. Sin esto, nadie recibe aviso: la bandeja de arriba es el respaldo."
            >
              <Input
                value={correos}
                onChange={(e) => setCorreos(e.target.value)}
                placeholder="bienestar@empresa.com, rrhh@empresa.com"
              />
            </Field>
          </div>

          <section>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h3 className="text-3 font-bold text-brand-ink">
                Campos <span className="font-normal text-muted">({campos.length})</span>
              </h3>
              <Button
                variant="secondary"
                size="sm"
                className="ml-auto"
                loading={sugerir.isPending}
                onClick={() => sugerir.mutate(
                  {
                    target_kind: destino,
                    ...(destino === 'resource' ? { target_resource_id: recursoDestino } : {}),
                  },
                  {
                    onSuccess: (r) => {
                      if (!r.fields?.length) {
                        Utils.toast('Este destino no propone campos; agrégalos a mano', 'info');
                        return;
                      }
                      setCampos(r.fields.map((c) => ({ ...c })));
                    },
                    onError: (err) => Utils.toast(
                      err instanceof Error ? err.message : 'No se pudieron proponer', 'error'),
                  },
                )}
              >
                <i className="bi bi-magic" aria-hidden="true" /> Proponer los del destino
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCampos((cs) => [
                  ...cs, { key: '', label: '', type: 'text', required: false, options: [] },
                ])}
              >
                <i className="bi bi-plus-lg" aria-hidden="true" /> Campo
              </Button>
            </div>

            {campos.length ? (
              <ul className="flex flex-col gap-2">
                {campos.map((c, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2 rounded-sm border border-line p-2">
                    <InputSuelto
                      aria-label={`Etiqueta del campo ${i + 1}`}
                      className="min-w-[9rem] flex-1 py-1 text-2"
                      value={c.label ?? ''}
                      onChange={(e) => setCampos((cs) => cs.map(
                        (x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                      placeholder="Etiqueta"
                    />
                    <SelectSuelto
                      aria-label={`Tipo del campo ${i + 1}`}
                      className="w-auto py-1 text-2"
                      value={c.type}
                      onChange={(e) => setCampos((cs) => cs.map(
                        (x, j) => (j === i ? { ...x, type: e.target.value as TipoBasico } : x)))}
                    >
                      {TIPOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </SelectSuelto>

                    {c.type === 'select' && (
                      <SelectSuelto
                        aria-label={`Origen de las opciones del campo ${i + 1}`}
                        className="w-auto py-1 text-2"
                        value={c.catalog_source ?? ''}
                        onChange={(e) => setCampos((cs) => cs.map(
                          (x, j) => (j === i
                            ? { ...x, catalog_source: e.target.value || undefined }
                            : x)))}
                      >
                        <option value="">— Opciones a mano —</option>
                        {(catalogos.data ?? []).map((s) => (
                          <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                      </SelectSuelto>
                    )}
                    {c.type === 'select' && !c.catalog_source && (
                      <InputSuelto
                        aria-label={`Opciones del campo ${i + 1}`}
                        className="w-32 py-1 text-2"
                        value={(c.options ?? []).join(', ')}
                        onChange={(e) => setCampos((cs) => cs.map(
                          (x, j) => (j === i
                            ? { ...x, options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }
                            : x)))}
                        placeholder="opc1, opc2"
                      />
                    )}
                    {c.type === 'select' && c.catalog_source && (
                      <span className="text-1 text-muted">
                        <i className="bi bi-link-45deg" aria-hidden="true" /> del catálogo
                      </span>
                    )}

                    <label className="flex items-center gap-1 whitespace-nowrap text-1 text-ink-2">
                      <input
                        type="checkbox"
                        checked={!!c.required}
                        onChange={(e) => setCampos((cs) => cs.map(
                          (x, j) => (j === i ? { ...x, required: e.target.checked } : x)))}
                      />
                      obligatorio
                    </label>

                    {c.map_to && (
                      <Badge tone="info" title={`Se aplica a la columna «${c.map_to}» del destino`}>
                        <i className="bi bi-box-arrow-in-down" aria-hidden="true" /> {c.map_to}
                      </Badge>
                    )}

                    <Button
                      variant="ghost" size="sm" iconOnly
                      aria-label={`Quitar el campo ${c.label || i + 1}`}
                      onClick={() => setCampos((cs) => cs.filter((_, j) => j !== i))}
                    >
                      <i className="bi bi-x-lg text-danger-ink" aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-2 text-muted">
                Sin campos todavía. Usa «Proponer los del destino» o agrégalos a mano.
              </p>
            )}
          </section>
        </div>
      )}

      {id != null && (
        <ConfirmDialog
          abierto={borrando}
          onCambio={setBorrando}
          titulo="Eliminar el formulario"
          descripcion="Deja de poder diligenciarse. Las respuestas ya enviadas se conservan en la auditoría."
          textoConfirmar="Eliminar"
          destructivo
          cargando={borrar.isPending}
          onConfirmar={() => borrar.mutate(id, {
            onSuccess: () => { Utils.toast('Formulario eliminado', 'success'); setBorrando(false); onCerrar(); },
            onError: (err) => {
              setBorrando(false);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
            },
          })}
        />
      )}
    </Drawer>
  );
}
